import { useDotLegalSnackbar } from "@dotlegal/dotlegal-ui-components";
import { useTranslation } from "@localization/useTranslation";
import { post } from "@common/api/apiShared";
import { Result } from "@common/api/result";
import { useDotLegalMutation, useDotLegalQuery } from "@common/hooks/dotLegalReactQuery";
import { useRiskAreas } from "@common/hooks/useSelectableItemQueries";
import { IRiskConsequenceCatalogDialog } from "./RiskConsequenceCatalogDialog";
import { ConsequenceCatalogItemViewModel, ImportConsequenceCatalogModel } from "./RiskConsequenceCatalogDialog.types";
import { allOptionId } from "../../common/RiskSettings.utils";
import { SelectableCatalogRow, useCatalogEntrySelection, useSelectionWithAllByEntry } from "../../common/CatalogSelection.hooks";

export type { SelectableCatalogRow };

export function useRiskConsequenceCatalogDialogDataMapping(props: IRiskConsequenceCatalogDialog) {
    const snackbar = useDotLegalSnackbar();
    const { translateString, translateRiskConsequenceCategory } = useTranslation();
    const { isLoading: riskAreasLoading, riskAreas } = useRiskAreas();
    const { selectedEntries, toggleEntry, onSelectAllChanged, getHeaderCheckBoxState, hasSelection } = useCatalogEntrySelection();
    const riskAreaSelection = useSelectionWithAllByEntry();

    const { isLoading, data } = useDotLegalQuery<Array<ConsequenceCatalogItemViewModel>>("/risk-consequences/catalog");

    const rows: Array<SelectableCatalogRow> = (data ?? []).map((item) => ({
        entry: item.entry,
        name: item.name,
        description: item.description,
        categoryLabel: translateRiskConsequenceCategory(item.category),
        isSelected: selectedEntries.includes(item.entry),
    }));

    const allEntries = rows.map((row) => row.entry);

    const riskAreaOptions = [{ id: allOptionId, name: translateString("allRiskAreas") }, ...(riskAreas ?? [])];

    const importMutation = useDotLegalMutation(importEntries, {
        onSuccess: (response: Result<{ imported: number }>) => {
            props.onImported();
            props.onHideDialog();
            snackbar.show(translateString("riskConsequencesImported", { count: response.value().imported }));
        },
    });

    function importEntries() {
        const model: ImportConsequenceCatalogModel = {
            items: selectedEntries.map((entry) => ({ entry, riskAreaIds: riskAreaSelection.valuesByEntry[entry] ?? [] })),
        };
        return post<{ imported: number }>("/risk-consequences/catalog/import", model);
    }

    return {
        isLoading,
        rows,
        toggleEntry,
        onSelectAllChanged: () => onSelectAllChanged(allEntries),
        getHeaderCheckBoxState: () => getHeaderCheckBoxState(allEntries),
        riskAreaOptions,
        riskAreasLoading,
        getChosenRiskAreas: riskAreaSelection.getChosen,
        setRiskAreas: riskAreaSelection.set,
        hasSelection,
        inProgress: importMutation.isLoading,
        importSelected: () => importMutation.mutateAsync(),
    };
}
